import { useEffect, useState } from 'react';
import { Search, AlertTriangle, CheckCircle2, XCircle, Building2, Package, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useWizardStore, useWizardActions } from '@/stores';
import { useValidateBridge } from '@/features/migrations/hooks';
import type { BridgeValidationResponse, BridgeDuplicateInfo } from '@/lib/api';

const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

interface DuplicateWarningProps {
  duplicate: BridgeDuplicateInfo;
}

function DuplicateWarning({ duplicate }: DuplicateWarningProps) {
  return (
    <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
      <div className="flex items-start gap-3">
        <XCircle className="h-5 w-5 text-destructive mt-0.5" />
        <div className="flex-1">
          <p className="font-medium text-destructive">
            {duplicate.displayName} - Duplicate Detected
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Label ID: {duplicate.labelId}
          </p>
          <div className="mt-2">
            <p className="text-sm text-muted-foreground">Conflicting entities:</p>
            <ul className="mt-1 space-y-1">
              {duplicate.conflictingEntities.map((entity) => (
                <li key={entity.entityId} className="text-sm">
                  • {entity.entityName}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Bridge ProductType ids that get special handling in this step.
// Keep in sync with MigrationOrchestrator.Domain.Enums.BridgeProductType.
const PRODUCT_TYPE_PROJECT_UNITY = 27;
const PRODUCT_TYPE_LANDING_PAGE = 26;

export function BridgeLookupStep() {
  const bridgeEntityUuid = useWizardStore((s) => s.bridgeEntityUuid);
  const bridgeEntity = useWizardStore((s) => s.bridgeEntity);
  const matchedModules = useWizardStore((s) => s.matchedModules);
  const specialModules = useWizardStore((s) => s.specialModules);
  const organizationType = useWizardStore((s) => s.organizationType);
  const { setBridgeResult, clearBridgeResult, setIsValid, setBridgeEntityUuid } = useWizardActions();

  const [uuid, setUuid] = useState(bridgeEntityUuid ?? '');
  const [uuidError, setUuidError] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<BridgeValidationResponse | null>(null);

  const { mutate: validateBridge, isPending, error: apiError } = useValidateBridge({
    onSuccess: (result) => {
      setValidationResult(result);
      if (result.valid && result.entity) {
        setBridgeResult(result.entity, result.matchedModules, result.specialModules ?? []);
        setIsValid(true);
      } else {
        clearBridgeResult();
        setIsValid(false);
      }
    },
    onError: () => {
      clearBridgeResult();
      setIsValid(false);
    },
  });

  const hasProjectUnity = specialModules.some(
    (m) => m.bridgeProductTypeId === PRODUCT_TYPE_PROJECT_UNITY,
  );
  const hasLandingPage = specialModules.some(
    (m) => m.bridgeProductTypeId === PRODUCT_TYPE_LANDING_PAGE,
  );
  const projectUnityBlocksNewOrg = organizationType === 'New' && hasProjectUnity;

  // Update validity based on current state. ProjectUnity on the Bridge entity means the
  // org is already in Amplify, so the New Org flow must be blocked even when matched
  // modules look fine — the operator should switch to the Existing Organization flow.
  useEffect(() => {
    const isValid =
      bridgeEntity !== undefined &&
      matchedModules.length > 0 &&
      !projectUnityBlocksNewOrg;
    setIsValid(isValid);
  }, [bridgeEntity, matchedModules, projectUnityBlocksNewOrg, setIsValid]);

  const handleUuidChange = (value: string) => {
    setUuid(value);
    setBridgeEntityUuid(value);
    setUuidError(null);
    setValidationResult(null);
  };

  const handleValidate = () => {
    if (!uuid.trim()) {
      setUuidError('Bridge entity UUID is required');
      return;
    }

    if (!UUID_REGEX.test(uuid)) {
      setUuidError('Invalid UUID format');
      return;
    }

    setUuidError(null);
    validateBridge({ bridgeEntityUuid: uuid });
  };

  const handleRetry = () => {
    setValidationResult(null);
    handleValidate();
  };

  const hasDuplicates = validationResult?.duplicates && validationResult.duplicates.length > 0;
  const hasNoMatchingModules = validationResult?.error === 'BRIDGE_NO_MATCHING_MODULES';
  const entityNotFound = validationResult?.error === 'BRIDGE_ENTITY_NOT_FOUND';
  const hasMinistryOneWithoutGiving = validationResult?.error === 'BRIDGE_MINISTRY_ONE_WITHOUT_GIVING';

  const formBuilderModules = specialModules.filter(
    (m) => m.moduleKey === 'formbuilder' || m.moduleKey === 'formbuilder_client',
  );
  const hasFormBuilder = formBuilderModules.length > 0;

  const ministryOneModule = specialModules.find(
    (m) => m.moduleKey === 'ministryone',
  );
  const showMinistryOneInfoCard =
    ministryOneModule?.duplicateCheck === 'clean' && validationResult?.valid === true;

  return (
    <div className="space-y-6">
      {/* UUID Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Bridge Entity Lookup
          </CardTitle>
          <CardDescription>
            Enter the Bridge entity UUID to identify the customer and their available modules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="bridge-uuid">Bridge Entity UUID</Label>
            <div className="flex gap-2">
              <Input
                id="bridge-uuid"
                type="text"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={uuid}
                onChange={(e) => handleUuidChange(e.target.value)}
                className={uuidError ? 'border-destructive' : ''}
                disabled={isPending}
              />
              <Button
                onClick={handleValidate}
                disabled={isPending || !uuid.trim()}
              >
                {isPending ? 'Validating...' : 'Lookup'}
              </Button>
            </div>
            {uuidError && (
              <p className="text-sm text-destructive">{uuidError}</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Error States */}
      {apiError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Lookup Failed</AlertTitle>
          <AlertDescription>
            Unable to connect to Bridge API. Please try again later.
          </AlertDescription>
        </Alert>
      )}

      {entityNotFound && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Entity Not Found</AlertTitle>
          <AlertDescription>
            Bridge entity not found. Please verify the UUID is correct.
          </AlertDescription>
        </Alert>
      )}

      {hasNoMatchingModules && validationResult?.entity && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No Migratable Modules</AlertTitle>
          <AlertDescription>
            The customer "{validationResult.entity.name}" does not have any modules configured for migration.
          </AlertDescription>
        </Alert>
      )}

      {/* Blocking Errors: Duplicates and MinistryOne */}
      {(hasDuplicates || hasMinistryOneWithoutGiving) && (
        <div className="space-y-4">
          {hasDuplicates && (
            <>
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Duplicate Bridge Entries Found</AlertTitle>
                <AlertDescription>
                  Migration cannot proceed because duplicate entries were detected. Please resolve the duplicates in Bridge before continuing.
                </AlertDescription>
              </Alert>

              <div className="space-y-3">
                {validationResult?.duplicates?.map((dup) => (
                  <DuplicateWarning key={dup.moduleKey} duplicate={dup} />
                ))}
              </div>
            </>
          )}

          {hasMinistryOneWithoutGiving && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>MinistryOne Requires Giving</AlertTitle>
              <AlertDescription>
                MinistryOne was detected on this Bridge entity but Giving is not present.
                Please add Giving to this Bridge entity before proceeding.
              </AlertDescription>
            </Alert>
          )}

          <Button variant="outline" onClick={handleRetry}>
            Retry Validation
          </Button>
        </div>
      )}

      {/* ProjectUnity + New Org: blocking. ProjectUnity (BridgeProductType 27) is the
          Amplify target platform — its presence means the org is already in Amplify, so
          we can't run the Create-New-Organization flow against this Bridge record. */}
      {projectUnityBlocksNewOrg && validationResult?.valid && validationResult.entity && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Project Unity Detected — Use Existing Organization Flow</AlertTitle>
          <AlertDescription>
            This Bridge record already has Project Unity (Amplify) provisioned, so a new
            organization cannot be created from it. Go back to step 1 and choose
            "Existing Organization" to migrate against the existing Amplify org instead.
          </AlertDescription>
        </Alert>
      )}

      {/* Success State */}
      {validationResult?.valid && validationResult.entity && !projectUnityBlocksNewOrg && (
        <div className="space-y-4">
          <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertTitle className="text-green-600">Customer Verified</AlertTitle>
            <AlertDescription className="text-green-700 dark:text-green-400">
              Bridge entity found and validated successfully.
            </AlertDescription>
          </Alert>

          {/* Entity Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">Organization Name</p>
                  <p className="font-medium">{validationResult.entity.name}</p>
                </div>
                {validationResult.entity.addressLine1 && (
                  <div>
                    <p className="text-sm text-muted-foreground">Address</p>
                    <p className="font-medium">
                      {validationResult.entity.addressLine1}
                      {validationResult.entity.city && `, ${validationResult.entity.city}`}
                      {validationResult.entity.state && `, ${validationResult.entity.state}`}
                      {validationResult.entity.zip && ` ${validationResult.entity.zip}`}
                    </p>
                  </div>
                )}
                {validationResult.entity.contactEmail && (
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Email</p>
                    <p className="font-medium">{validationResult.entity.contactEmail}</p>
                  </div>
                )}
                {(validationResult.entity.contactFirstName || validationResult.entity.contactLastName) && (
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Name</p>
                    <p className="font-medium">
                      {validationResult.entity.contactFirstName} {validationResult.entity.contactLastName}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Matched Modules Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Available Modules
              </CardTitle>
              <CardDescription>
                {matchedModules.length} module{matchedModules.length !== 1 ? 's' : ''} available for migration
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {matchedModules.map((module) => (
                  <div
                    key={module.moduleKey}
                    className="flex items-center justify-between rounded-lg border p-3"
                  >
                    <div>
                      <p className="font-medium">{module.displayName}</p>
                      <p className="text-sm text-muted-foreground">
                        Source ID: {module.labelId}
                      </p>
                    </div>
                    <Badge variant="secondary">{module.productLabel}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* LandingPage / ProjectUnity informational note — these live on the Bridge
              entity but aren't migrated by the orchestrator (LandingPage is out-of-band;
              ProjectUnity is Amplify itself). We surface them so the operator knows we
              saw them and chose not to migrate them. ProjectUnity in the New Org flow is
              handled separately by the blocking alert above. */}
          {(hasLandingPage || hasProjectUnity) && (
            <div className="flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <p>
                {hasLandingPage && hasProjectUnity
                  ? 'LandingPage and Project Unity are present on this Bridge entity but are not migrated by the orchestrator.'
                  : hasLandingPage
                    ? 'LandingPage is present on this Bridge entity but is not migrated by the orchestrator.'
                    : 'Project Unity is present on this Bridge entity (the Amplify target platform) — modules will be migrated into the existing Amplify organization.'}
              </p>
            </div>
          )}

          {/* Form Builder informational note — kept lightweight on purpose. These products
              live on the Bridge entity but are not migrated independently, so we mention
              them in passing rather than treating them like a real module list. */}
          {hasFormBuilder && (
            <div className="flex items-start gap-2 rounded-md bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              <p>
                <span className="font-medium text-foreground">
                  {formBuilderModules.map((m) => m.displayName).join(', ')}
                </span>{' '}
                {formBuilderModules.length === 1 ? 'is' : 'are'} present on this Bridge entity
                but {formBuilderModules.length === 1 ? "doesn't" : "don't"} migrate as a
                standalone module — included automatically with the parent product.
              </p>
            </div>
          )}

          {/* MinistryOne informational card */}
          {showMinistryOneInfoCard && ministryOneModule && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  MinistryOne
                </CardTitle>
                <CardDescription>
                  MinistryOne is automatically migrated as part of the Giving module migration.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <p className="font-medium">{ministryOneModule.displayName}</p>
                    <p className="text-sm text-muted-foreground">Label ID: {ministryOneModule.labelId}</p>
                  </div>
                  <Badge variant="secondary">{ministryOneModule.productLabel}</Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Unmatched Products Warning */}
          {validationResult.unmatchedProducts.length > 0 && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Some Products Not Available</AlertTitle>
              <AlertDescription>
                The following products are not configured for migration:
                <ul className="mt-2 list-disc list-inside">
                  {validationResult.unmatchedProducts.map((product) => (
                    <li key={product.labelId}>
                      {product.productLabel} (Label ID: {product.labelId})
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}
    </div>
  );
}
